/**
 * "Unrecognized from the last run" panel for the Photo List Settings page —
 * recon-board text the latest run saw that matches no rule. Cars carrying it
 * are still on the list (unknown values are never dropped, by design) but sit
 * colorless at the bottom; each row is a one-click fix: "Add as step" creates
 * the recon rule with the board's EXACT spelling (so slash-vs-no-slash drift
 * can't recur). The server pre-filters against current rules, so rows vanish
 * as rules are added; the ✕ hides a row for this visit only. Stargard
 * addresses with no rule moved into Address Aliases (2026-09-23), under the
 * store file that said them. Panel chrome from DealerPlatesView.tsx ~500-519.
 */
import React, { useState } from "react"
import { X } from "lucide-react"

import { PhotoListSettings, createReconStep } from "../../api/photoList"
import { THEME } from "../../config/theme"

interface Props {
  unrecognized: PhotoListSettings["unrecognized"]
  /** Adding a rule on an unseeded cluster would flip that whole cluster onto
   * one database row, so the button only renders once it is customized. */
  reconEditable: boolean
  dealerGroupId: number
  onRuleAdded: () => void
  onError: (message: string | null) => void
}

export default function UnrecognizedPanel({
  unrecognized,
  reconEditable,
  dealerGroupId,
  onRuleAdded,
  onError,
}: Props) {
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set())
  const [busyKey, setBusyKey] = useState<string | null>(null)

  const steps = unrecognized.recon_steps.filter((s) => !dismissed.has(`step:${s.name}`))
  if (steps.length === 0) return null

  const dismiss = (key: string) =>
    setDismissed((prev) => new Set(prev).add(key))

  const addStep = async (name: string) => {
    setBusyKey(`step:${name}`)
    onError(null)
    try {
      await createReconStep({ name }, dealerGroupId)
      onRuleAdded()
    } catch (err) {
      onError(err instanceof Error ? err.message : "Failed to add recon step")
    } finally {
      setBusyKey(null)
    }
  }

  return (
    <section style={panelStyle}>
      <div style={panelTitleStyle}>Unrecognized from the last run</div>
      <p style={panelSubtitleStyle}>
        The feeds said these; no rule knows them yet. The cars are still on
        the list — colorless, at the bottom of their sheet.
        {!reconEditable && " Customize this group's settings above to add rules for them."}
      </p>

      {steps.length > 0 && (
        <>
          <div style={groupHeaderStyle}>Recon steps</div>
          {steps.map((step) => (
            <div key={step.name} style={rowStyle}>
              <span style={rawTextStyle}>{step.name}</span>
              <span style={countStyle}>
                {step.cars} car{step.cars === 1 ? "" : "s"}
              </span>
              {reconEditable ? (
                <button
                  type="button"
                  disabled={busyKey === `step:${step.name}`}
                  onClick={() => addStep(step.name)}
                  style={addButtonStyle(busyKey === `step:${step.name}`)}
                >
                  {busyKey === `step:${step.name}` ? "Adding…" : "Add as step"}
                </button>
              ) : (
                <span />
              )}
              <button
                type="button"
                aria-label={`Dismiss ${step.name}`}
                onClick={() => dismiss(`step:${step.name}`)}
                style={dismissButtonStyle}
              >
                <X size={14} strokeWidth={2} />
              </button>
            </div>
          ))}
        </>
      )}

    </section>
  )
}

// --- Local styles (panel chrome from DealerPlatesView.tsx ~lines 500-519) ---

const panelStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  border: `1px solid ${THEME.colors.semantic.warning}`,
  borderRadius: THEME.borderRadius.md,
  padding: THEME.spacing.md,
  marginBottom: THEME.spacing.lg,
}

const panelTitleStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 500,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: 4,
}

const panelSubtitleStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: 0,
  marginBottom: THEME.spacing.md,
  lineHeight: 1.4,
}

const groupHeaderStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
  marginTop: THEME.spacing.sm,
  marginBottom: THEME.spacing.xs,
}

const rowStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) auto auto 32px",
  gap: THEME.spacing.sm,
  alignItems: "center",
  padding: `${THEME.spacing.xs} 0`,
  borderTop: `1px solid ${THEME.colors.silver}`,
}

const rawTextStyle: React.CSSProperties = {
  fontFamily: THEME.fonts.mono,
  fontSize: 12,
  overflowWrap: "anywhere",
  color: THEME.colors.neutral.light.textPrimary,
}

const countStyle: React.CSSProperties = {
  fontSize: 12,
  whiteSpace: "nowrap",
  color: THEME.colors.neutral.light.textSecondary,
}

const addButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: `6px ${THEME.spacing.sm}`,
  fontSize: 12,
  fontWeight: 500,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  border: "none",
  borderRadius: THEME.borderRadius.sm,
  cursor: disabled ? "not-allowed" : "pointer",
  opacity: disabled ? 0.5 : 1,
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})

const dismissButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "2px 6px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
}
